import { useEffect, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import {
  BedarfStatus,
  BedarfPrioritaet,
  CreateBedarfDto,
  Bedarf,
  GenderConfig,
  calculateWorkingHoursFromTimeRange,
  formatHours,
} from '../../types/bedarf';
import { StadtSelect } from '../common/StadtSelect';
import TagesArbeitszeit from './TagesArbeitszeit';
import { buildBedarfFormValues } from './bedarfFormValues';

interface BedarfeFormProps {
  initialData?: Bedarf;
  /**
   * Values from a saved draft. They override `initialData` field by field, so
   * a draft written by an older form version still fills what it knows.
   */
  draftValues?: Partial<CreateBedarfDto>;
  /** Called with the raw form values on every change, typing and selects alike. */
  onValuesChange?: (values: CreateBedarfDto) => void;
  /** Shown left of the action buttons. */
  footerHint?: ReactNode;
  onSubmit: (data: CreateBedarfDto) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function BedarfeForm({
  initialData,
  draftValues,
  onValuesChange,
  footerHint,
  onSubmit,
  onCancel,
  isLoading,
}: BedarfeFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateBedarfDto>({
    defaultValues: { ...buildBedarfFormValues(initialData), ...draftValues },
  });

  // `watch` with a callback fires for typing and for setValue (the city
  // selects) alike, without re-rendering the form on every keystroke.
  useEffect(() => {
    if (!onValuesChange) return;
    const subscription = watch((values) => onValuesChange(values as CreateBedarfDto));
    return () => subscription.unsubscribe();
  }, [watch, onValuesChange]);

  // Watch time fields for total calculation
  const startTimeMonday = watch('startTimeMonday');
  const endTimeMonday = watch('endTimeMonday');
  const startTimeTuesday = watch('startTimeTuesday');
  const endTimeTuesday = watch('endTimeTuesday');
  const startTimeWednesday = watch('startTimeWednesday');
  const endTimeWednesday = watch('endTimeWednesday');
  const startTimeThursday = watch('startTimeThursday');
  const endTimeThursday = watch('endTimeThursday');
  const startTimeFriday = watch('startTimeFriday');
  const endTimeFriday = watch('endTimeFriday');

  // Calculate daily hours from time ranges
  const hoursMonday = calculateWorkingHoursFromTimeRange(startTimeMonday, endTimeMonday);
  const hoursTuesday = calculateWorkingHoursFromTimeRange(startTimeTuesday, endTimeTuesday);
  const hoursWednesday = calculateWorkingHoursFromTimeRange(startTimeWednesday, endTimeWednesday);
  const hoursThursday = calculateWorkingHoursFromTimeRange(startTimeThursday, endTimeThursday);
  const hoursFriday = calculateWorkingHoursFromTimeRange(startTimeFriday, endTimeFriday);

  const calculatedTotal = hoursMonday + hoursTuesday + hoursWednesday + hoursThursday + hoursFriday;

  const handleFormSubmit = async (data: CreateBedarfDto) => {
    // Clean up empty strings to undefined
    const cleanedData: CreateBedarfDto = {
      // Stammdaten
      childFirstName: data.childFirstName,
      childLastName: data.childLastName,
      childDateOfBirth: data.childDateOfBirth || undefined,
      childGender: data.childGender || undefined,
      childStreet: data.childStreet || undefined,
      childCityId: data.childCityId || undefined,
      childPostalCode: data.childPostalCode || undefined,

      // Schulische Angaben
      schoolName: data.schoolName || undefined,
      schoolStreet: data.schoolStreet || undefined,
      schoolCityId: data.schoolCityId,
      schoolPostalCode: data.schoolPostalCode || undefined,
      schoolClass: data.schoolClass || undefined,

      // Bedarf & Diagnose
      diagnosis: data.diagnosis || undefined,
      supportDescription: data.supportDescription || undefined,
      specialNeeds: data.specialNeeds || undefined,

      // Stundenplan - Start/End times per day
      startTimeMonday: data.startTimeMonday || undefined,
      endTimeMonday: data.endTimeMonday || undefined,
      startTimeTuesday: data.startTimeTuesday || undefined,
      endTimeTuesday: data.endTimeTuesday || undefined,
      startTimeWednesday: data.startTimeWednesday || undefined,
      endTimeWednesday: data.endTimeWednesday || undefined,
      startTimeThursday: data.startTimeThursday || undefined,
      endTimeThursday: data.endTimeThursday || undefined,
      startTimeFriday: data.startTimeFriday || undefined,
      endTimeFriday: data.endTimeFriday || undefined,
      includesBreaksOgs: data.includesBreaksOgs,

      // Rahmenbedingungen
      startDateRequested: data.startDateRequested || undefined,
      endDateRequested: data.endDateRequested || undefined,
      currentSituation: data.currentSituation || undefined,

      // Kostenträger / Formalien
      kostentraegerName: data.kostentraegerName || undefined,
      kostentraegerAnsprechpartner: data.kostentraegerAnsprechpartner || undefined,
      kostentraegerEmail: data.kostentraegerEmail || undefined,
      kostentraegerPhone: data.kostentraegerPhone || undefined,
      hasApproval: data.hasApproval,
      hasCarePlan: data.hasCarePlan,
      legalBasis: data.legalBasis || undefined,

      // Elternkontakt
      parentName: data.parentName || undefined,
      parentPhone: data.parentPhone || undefined,
      parentEmail: data.parentEmail || undefined,

      // Meta
      status: data.status,
      priority: data.priority,
      notes: data.notes || undefined,
    };
    await onSubmit(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Stammdaten */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Stammdaten des Kindes</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label htmlFor="childFirstName" className="block text-sm font-medium text-gray-700">
                Vorname *
              </label>
              <input
                type="text"
                id="childFirstName"
                {...register('childFirstName', { required: 'Vorname ist erforderlich' })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              {errors.childFirstName && (
                <p className="mt-1 text-sm text-red-600">{errors.childFirstName.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="childLastName" className="block text-sm font-medium text-gray-700">
                Nachname
              </label>
              <input
                type="text"
                id="childLastName"
                {...register('childLastName')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="childDateOfBirth" className="block text-sm font-medium text-gray-700">
                Geburtsdatum
              </label>
              <input
                type="date"
                id="childDateOfBirth"
                {...register('childDateOfBirth')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="childGender" className="block text-sm font-medium text-gray-700">
                Geschlecht
              </label>
              <select
                id="childGender"
                {...register('childGender')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">-- Bitte wählen --</option>
                {Object.entries(GenderConfig).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2 lg:col-span-4">
              <label htmlFor="childStreet" className="block text-sm font-medium text-gray-700">
                Straße
              </label>
              <input
                type="text"
                id="childStreet"
                {...register('childStreet')}
                placeholder="Musterstraße 123"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div className="sm:col-span-2 grid grid-cols-3 gap-2">
              <div>
                <label htmlFor="childPostalCode" className="block text-sm font-medium text-gray-700">
                  PLZ
                </label>
                <input
                  type="text"
                  id="childPostalCode"
                  {...register('childPostalCode')}
                  placeholder="50667"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div className="col-span-2">
                <StadtSelect
                  id="childCityId"
                  label="Stadt (Wohnort)"
                  value={watch('childCityId') || ''}
                  onChange={(id) => setValue('childCityId', id)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                {...register('status')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value={BedarfStatus.NOCH_NICHT_RELEVANT}>Noch nicht relevant</option>
                <option value={BedarfStatus.ANFRAGE}>Anfragen</option>
                <option value={BedarfStatus.OFFEN}>Offen</option>
                <option value={BedarfStatus.KENNENLERNGESPRAECH}>Kennenlerngespräch</option>
                <option value={BedarfStatus.TERMINIERT}>Terminiert</option>
                <option value={BedarfStatus.KOSTENTRAEGER_GO}>Kostenträger Go</option>
                <option value={BedarfStatus.ONBOARDING}>Onboarding</option>
                <option value={BedarfStatus.GEWONNEN}>Gewonnen</option>
                <option value={BedarfStatus.VERLOREN}>Verloren</option>
              </select>
            </div>

            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                Priorität
              </label>
              <select
                id="priority"
                {...register('priority')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value={BedarfPrioritaet.NIEDRIG}>Niedrig</option>
                <option value={BedarfPrioritaet.MITTEL}>Mittel</option>
                <option value={BedarfPrioritaet.HOCH}>Hoch</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Schulische Angaben */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Schulische Angaben</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
            <div className="sm:col-span-3">
              <label htmlFor="schoolName" className="block text-sm font-medium text-gray-700">
                Schulname
              </label>
              <input
                type="text"
                id="schoolName"
                {...register('schoolName')}
                placeholder="Grundschule am Park"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="schoolClass" className="block text-sm font-medium text-gray-700">
                Klasse
              </label>
              <input
                type="text"
                id="schoolClass"
                {...register('schoolClass')}
                placeholder="3a"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="schoolStreet" className="block text-sm font-medium text-gray-700">
                Straße
              </label>
              <input
                type="text"
                id="schoolStreet"
                {...register('schoolStreet')}
                placeholder="Schulstraße 1"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div className="sm:col-span-2 grid grid-cols-3 gap-2">
              <div>
                <label htmlFor="schoolPostalCode" className="block text-sm font-medium text-gray-700">
                  PLZ
                </label>
                <input
                  type="text"
                  id="schoolPostalCode"
                  {...register('schoolPostalCode')}
                  placeholder="50667"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div className="col-span-2">
                <StadtSelect
                  id="schoolCityId"
                  label="Stadt (Schule) *"
                  value={watch('schoolCityId') || ''}
                  onChange={(id) => setValue('schoolCityId', id)}
                />
                {errors.schoolCityId && (
                  <p className="mt-1 text-sm text-red-600">{errors.schoolCityId.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bedarf & Diagnose */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Bedarf & Diagnose</h3>
        </div>
        <div className="px-4 py-5 sm:p-6 space-y-4">
          <div>
            <label htmlFor="diagnosis" className="block text-sm font-medium text-gray-700">
              Diagnose
            </label>
            <textarea
              id="diagnosis"
              rows={2}
              {...register('diagnosis')}
              placeholder="ADHS, Autismus-Spektrum-Störung, etc."
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="supportDescription" className="block text-sm font-medium text-gray-700">
              Unterstützungsbedarf
            </label>
            <textarea
              id="supportDescription"
              rows={2}
              {...register('supportDescription')}
              placeholder="Unterstützung bei Konzentration und Sozialverhalten"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="specialNeeds" className="block text-sm font-medium text-gray-700">
              Besondere Anforderungen
            </label>
            <textarea
              id="specialNeeds"
              rows={2}
              {...register('specialNeeds')}
              placeholder="Benötigt regelmäßige Pausen, ruhige Umgebung"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Stundenplan */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Stundenplan</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {/* Monday */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Montag</label>
              <div className="flex items-center gap-1">
                <input
                  type="time"
                  {...register('startTimeMonday')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="time"
                  {...register('endTimeMonday')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <TagesArbeitszeit startTime={startTimeMonday} endTime={endTimeMonday} className="text-center" />
            </div>

            {/* Tuesday */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Dienstag</label>
              <div className="flex items-center gap-1">
                <input
                  type="time"
                  {...register('startTimeTuesday')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="time"
                  {...register('endTimeTuesday')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <TagesArbeitszeit startTime={startTimeTuesday} endTime={endTimeTuesday} className="text-center" />
            </div>

            {/* Wednesday */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Mittwoch</label>
              <div className="flex items-center gap-1">
                <input
                  type="time"
                  {...register('startTimeWednesday')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="time"
                  {...register('endTimeWednesday')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <TagesArbeitszeit startTime={startTimeWednesday} endTime={endTimeWednesday} className="text-center" />
            </div>

            {/* Thursday */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Donnerstag</label>
              <div className="flex items-center gap-1">
                <input
                  type="time"
                  {...register('startTimeThursday')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="time"
                  {...register('endTimeThursday')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <TagesArbeitszeit startTime={startTimeThursday} endTime={endTimeThursday} className="text-center" />
            </div>

            {/* Friday */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Freitag</label>
              <div className="flex items-center gap-1">
                <input
                  type="time"
                  {...register('startTimeFriday')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="time"
                  {...register('endTimeFriday')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <TagesArbeitszeit startTime={startTimeFriday} endTime={endTimeFriday} className="text-center" />
            </div>
          </div>

          {/* Weekly Total and Options */}
          <div className="mt-4 flex items-center justify-between border-t pt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                {...register('includesBreaksOgs')}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">inkl. Pausen/OGS</span>
            </label>
            <div className="text-right">
              <span className="text-sm text-gray-500">Wochenstunden: </span>
              <span className="font-medium text-gray-900">{formatHours(calculatedTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Rahmenbedingungen */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Rahmenbedingungen</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="startDateRequested" className="block text-sm font-medium text-gray-700">
                Gewünschter Beginn
              </label>
              <input
                type="date"
                id="startDateRequested"
                {...register('startDateRequested')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="endDateRequested" className="block text-sm font-medium text-gray-700">
                Voraussichtliches Ende
              </label>
              <input
                type="date"
                id="endDateRequested"
                {...register('endDateRequested')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="currentSituation" className="block text-sm font-medium text-gray-700">
                Aktuelle Situation
              </label>
              <textarea
                id="currentSituation"
                rows={2}
                {...register('currentSituation')}
                placeholder="Kind ist aktuell ohne Begleitung"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Kostenträger / Formalien */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Kostenträger / Formalien</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label htmlFor="kostentraegerName" className="block text-sm font-medium text-gray-700">
                Kostenträger
              </label>
              <input
                type="text"
                id="kostentraegerName"
                {...register('kostentraegerName')}
                placeholder="Jugendamt Köln"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="kostentraegerAnsprechpartner" className="block text-sm font-medium text-gray-700">
                Ansprechpartner
              </label>
              <input
                type="text"
                id="kostentraegerAnsprechpartner"
                {...register('kostentraegerAnsprechpartner')}
                placeholder="Frau Schmidt"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="kostentraegerEmail" className="block text-sm font-medium text-gray-700">
                E-Mail
              </label>
              <input
                type="email"
                id="kostentraegerEmail"
                {...register('kostentraegerEmail', {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Ungültige E-Mail-Adresse',
                  },
                })}
                placeholder="schmidt@jugendamt-koeln.de"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              {errors.kostentraegerEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.kostentraegerEmail.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="kostentraegerPhone" className="block text-sm font-medium text-gray-700">
                Telefon
              </label>
              <input
                type="tel"
                id="kostentraegerPhone"
                {...register('kostentraegerPhone')}
                placeholder="+49 221 12345"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="legalBasis" className="block text-sm font-medium text-gray-700">
                Rechtsgrundlage
              </label>
              <input
                type="text"
                id="legalBasis"
                {...register('legalBasis')}
                placeholder="SGB VIII §35a"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div className="flex items-center space-x-6 lg:col-span-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('hasApproval')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Bewilligung vorhanden</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('hasCarePlan')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Hilfeplan vorhanden</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Elternkontakt */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Elternkontakt</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <label htmlFor="parentName" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                id="parentName"
                {...register('parentName')}
                placeholder="Hans Mustermann"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="parentPhone" className="block text-sm font-medium text-gray-700">
                Telefon
              </label>
              <input
                type="tel"
                id="parentPhone"
                {...register('parentPhone')}
                placeholder="+49 171 9876543"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="parentEmail" className="block text-sm font-medium text-gray-700">
                E-Mail
              </label>
              <input
                type="email"
                id="parentEmail"
                {...register('parentEmail', {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Ungültige E-Mail-Adresse',
                  },
                })}
                placeholder="hans@example.com"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              {errors.parentEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.parentEmail.message}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Notizen</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <textarea
            id="notes"
            rows={4}
            {...register('notes')}
            placeholder="Zusätzliche Informationen zum Bedarf..."
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        {footerHint && <div className="mr-auto text-xs text-gray-500">{footerHint}</div>}
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Wird gespeichert...
            </>
          ) : (
            'Speichern'
          )}
        </button>
      </div>
    </form>
  );
}
